import { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
	getVideoAnalysisResults,
	getVideoProject,
	getVideoDetails,
	getVideoResults,
} from '@utils/api/videoProjects';

/** Exported for the dev preview page, which provides its own value. */
export const VideoProjectContext = createContext(null);

export const VideoProjectProvider = ({ children }) => {
	const { project_uuid: projectUuid } = useParams();
	const [project, setProject] = useState(null);
	// The run shown on the page: the latest unless the user picks another one. Kept in the URL (?job=) so a refresh or
	// a shared link shows the same version.
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedJobUuid = searchParams.get('job');
	const setSelectedJobUuid = useCallback(
		uuid =>
			setSearchParams(
				prev => {
					const next = new URLSearchParams(prev);
					if (uuid) next.set('job', uuid);
					else next.delete('job');
					return next;
				},
				{ replace: true }
			),
		[setSearchParams]
	);

	// Fetch video project data
	const {
		data: projectData,
		isLoading: isProjectLoading,
		error: projectError,
		refetch,
	} = useQuery({
		queryKey: ['videoProject', projectUuid],
		queryFn: () => getVideoProject(projectUuid),
		enabled: !!projectUuid,
	});

	useEffect(() => {
		if (projectData && projectData.success && projectData.data) {
			setProject(projectData.data);
		} else {
			setProject(null);
		}
	}, [projectData]);

	// Derive video UUID and latest job UUID from project
	const videoUuid = project?.video_uuids?.[0] || null; // currently single video per project
	const latestJobUuid = project?.jobs?.[project.jobs.length - 1] || null;
	const jobUuid =
		selectedJobUuid && project?.jobs?.includes(selectedJobUuid)
			? selectedJobUuid
			: latestJobUuid;

	// Fetch video details
	const { data: videoDetails } = useQuery({
		queryKey: ['videoDetails', projectUuid, videoUuid],
		queryFn: () => getVideoDetails(projectUuid, videoUuid),
		enabled: !!projectUuid && !!videoUuid,
	});

	const videoData = useMemo(() => {
		if (videoDetails?.success && videoDetails?.data) return videoDetails.data;
		return null;
	}, [videoDetails]);

	// Fetch results with jobUuid
	const { data: videoResults, isLoading: isResultsLoading } = useQuery({
		queryKey: ['videoResults', projectUuid, jobUuid],
		queryFn: () => getVideoResults(projectUuid, jobUuid),
		enabled: !!projectUuid && !!jobUuid,
		// Poll until the job ends: every 5 s while frames are processed (the live bar), else every 15 s. Keep
		// polling in a background tab and refetch on focus, so a finished job never stays shown as running (seen
		// on demo, 2026-10-19: the page sat at "Finding and tracking animals" until a hard refresh).
		refetchInterval: query => {
			const job = query.state.data?.data;
			if (job?.job_status === 'finished' || job?.job_status === 'failed') return false;
			return job?.job_status === 'running' && job?.progress >= 20 ? 5000 : 15000;
		},
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: 'always',
	});

	const resultsData = useMemo(() => {
		if (videoResults?.success && videoResults?.data) return videoResults.data;
		return null;
	}, [videoResults]);

	// Fetch analysis results with jobUuid when job status is finished
	const { data: analysisResults, isLoading: isAnalysisLoading } = useQuery({
		queryKey: ['analysisResults', projectUuid, jobUuid],
		queryFn: () => getVideoAnalysisResults(projectUuid, jobUuid),
		enabled: !!projectUuid && !!jobUuid && resultsData?.job_status === 'finished',
	});

	const analysisData = useMemo(() => {
		if (analysisResults?.success && analysisResults?.data) return analysisResults.data;
		return null;
	}, [analysisResults]);

	const contextValue = useMemo(
		() => ({
			project,
			projectUuid,
			isLoading: isProjectLoading,
			error: projectError,
			refetch,
			videoUuid,
			videoData,
			jobUuid,
			latestJobUuid,
			setSelectedJobUuid,
			resultsData,
			isResultsLoading,
			analysisData,
			isAnalysisLoading,
		}),
		[
			project,
			projectUuid,
			isProjectLoading,
			projectError,
			refetch,
			videoUuid,
			videoData,
			jobUuid,
			latestJobUuid,
			setSelectedJobUuid,
			resultsData,
			isResultsLoading,
			analysisData,
			isAnalysisLoading,
		]
	);

	return (
		<VideoProjectContext.Provider value={contextValue}>{children}</VideoProjectContext.Provider>
	);
};

export const useVideoProject = () => {
	const context = useContext(VideoProjectContext);
	if (!context) {
		throw new Error('useVideoProject must be used within VideoProjectProvider');
	}
	return context;
};
